import type { Copy } from './en';
import { LOCALE_INFO } from './locale';

/**
 * Korean copy. Terms follow the app's own app_ko.arb (여행, 체크인, 일정, 계획, 둘러보기,
 * 홈 베이스, 여권, 스탬프, 내 여행에 추가) so the site and app read the same.
 */

export const ko: Copy = {
  htmlLang: LOCALE_INFO.ko.htmlLang,
  phraseGap: ' ',
  ogLocale: 'ko_KR',
  languageName: '한국어',

  nav: {
    home: 'Travo 홈',
    primary: '메인 메뉴',
    features: '기능',
    plan: '여행 계획하기',
    about: '회사 소개',
    download: '앱 다운로드',
    openMenu: '메뉴 열기',
    closeMenu: '메뉴 닫기',
    language: '언어',
    byLabs: 'Travo Labs 제작'
  },

  download: {
    appStore: 'App Store에서 다운로드',
    playStore: 'Google Play에서 다운로드',
    ios: 'iOS(App Store)',
    android: 'Android(Google Play)',
    free: '무료 · iOS & ANDROID'
  },

  seo: {
    ogImage: '/og-image-ko.png',
    ogImageAlt: 'Travo — 삶은 빠르게 흘러가도, Travo가 기억합니다. iOS·Android용 여행 기록, 여권, 여행 계획 앱.',
    home: {
      title: 'Travo: 여행 기록·여행 일정 플래너 앱 | 여행 여권',
      description:
        'Travo는 무료 여행 기록 앱입니다. 집을 나서면 여행이 자동으로 기록되고, 새로운 나라마다 여권에 스탬프가 찍히며, 친구와 여행 일정도 함께 짤 수 있습니다. iOS·Android 지원.',
      keywords:
        '여행 기록 앱, 자동 여행 기록, 여행 여권, 방문한 나라, 여행 계획, 여행 플래너, 여행 일정 앱, 여행 일기, 체크인, 여행 지도, Travo'
    },
    about: {
      title: 'Travo 소개: 매일의 이야기를 간직하도록',
      description:
        '삶은 빠르게 흘러가지만, 소중한 순간까지 잊혀서는 안 되니까요. 그래서 Travo를 만들었습니다. Travo는 여행을 알아서 기록하고, 내가 고른 순간을 간직하며, 다음 여행 계획까지 도와줍니다.',
      keywords: 'Travo 소개, Travo Labs, 자동 여행 기록, 여행 여권, 여행 계획, 여행 일기, 개인정보 보호'
    }
  },

  hero: {
    eyebrow: '여행이 알아서 시작돼요',
    titleLead: '삶은 빠르게 흘러가도,',
    titleAccent: 'Travo가 기억합니다.',
    body: '집을 나서면 Travo가 여행을 시작합니다. 방문한 도시와 나라를 모두 기록하고, 여권에 스탬프를 찍고, 다음 여행 계획까지 챙겨 둡니다. 당신은 떠나기만 하면 됩니다.',
    photoAlt: '해 뜨는 계곡 위로 떠오른 열기구',
    phoneAlt: '사진 체크인 핀이 표시된 타이베이의 Travo 홈 지도와 여행·F1 지도 모드',
    stampLabel: '항공우편'
  },

  band: ['지도', '여행', '체크인', '장소', '친구', '여권', '일정', '둘러보기'],

  features: {
    title: '여덟 개의 정류장,\n하나의 여행 동반자.',
    body: '다녀온 곳을 기억하고, 소중한 순간을 간직하고, 다음 여행을 계획하세요. Travo와 함께하는 여정은 이 여덟 정류장에서 시작됩니다.',
    items: [
      {
        tag: '01 · 지도',
        title: '나의 여행을 한곳에',
        body: '여행, 저장한 장소, 체크인, 친구까지 하나의 지도에. 탭 한 번으로 레이어를 바꾸고, 다닐수록 채워지는 나만의 세계를 확인하세요.'
      },
      {
        tag: '02 · 여행',
        title: '떠나기만 하세요. Travo가 기억합니다.',
        body: '집을 나서면 여행이 자동으로 시작됩니다. 방문한 동네, 도시, 나라를 Travo가 기록하니 따로 입력할 필요가 없어요.'
      },
      {
        tag: '03 · 체크인',
        title: '이 순간을 간직하세요',
        body: '하루 최대 세 번 체크인할 수 있어요. 사진을 더해 그 순간과 그 장소를 기억하세요.'
      },
      {
        tag: '04 · 장소',
        title: '나중을 위해 저장하기',
        body: '다음 여행을 위해 장소를 리스트에 모으거나, 저장해 둔 Google 지도 리스트를 공유 한 번으로 Travo에 가져오세요.'
      },
      {
        tag: '05 · 친구',
        title: '여정을 함께 나누기',
        body: '친구의 최근 체크인을 확인하고, QR 코드를 스캔해 스탬프 카드를 주고받으세요.'
      },
      {
        tag: '06 · 여권',
        title: '추억이 담긴 여권',
        body: '방문하는 나라마다 스탬프를 모으세요. 항공편을 기록하고 지금까지 얼마나 날았는지 확인하세요.'
      },
      {
        tag: '07 · 일정',
        title: '함께 세우는 계획',
        body: '저장한 장소, 숙소, 항공편으로 일자별 일정을 만들어 보세요. 친구를 초대해 함께 계획할 수 있어요.'
      },
      {
        tag: '08 · 둘러보기',
        title: '다음 모험 찾기',
        body: '다른 여행자가 공유한 일정을 둘러보세요. 마음에 드는 일정을 내 여행에 추가하고 나만의 여행으로 바꿔 보세요.'
      }
    ],
    extrasLabel: '이 밖에도',
    extras: [
      '홈 화면 위젯: 여권, 예정된 여행, 일정',
      '공유하기 좋은 월간 리캡',
      'F1 플러그인: 레이스, 결과, 순위를 지도에서'
    ]
  },

  checkins: {
    eyebrow: '03 · 체크인',
    title: '하루에 도장 찍기.\n하루 최대 세 번.',
    body: '일상은 Travo가 알아서 기록합니다. 체크인은 당신이 고르는 순간이에요. 사진을 더하면 지도가 당신이 서 있던 바로 그곳을 기억합니다. 하루에 몇 번씩 쌓이면 나만의 여행 일기가 됩니다.',
    stamps: ['맛있는 한 끼', '멋진 풍경', '즐거운 모임'],
    screenshotAlt: '사진 체크인과 아이슬란드 여행이 표시된 2026년 6월 하이라이트 달력'
  },

  passport: {
    eyebrow: '06 · 여권',
    title: '방문한 나라마다 스탬프 하나.',
    body: '여행할수록 여권이 저절로 채워집니다. 해마다 스탬프 한 페이지, 방문한 나라와 여행한 날은 Travo가 세어 드려요.',
    points: [
      '해마다 한 페이지씩, 방문한 나라와 여행한 날까지 집계',
      '여권에 기록되는 항공편과 비행 거리(km)',
      '공유하기 좋은 사진과 장소의 월간 리캡',
      '홈 화면용 여권 위젯'
    ],
    sample: '샘플',
    thailand: '태국',
    indonesia: '인도네시아',
    japanAlt: '샘플 스탬프: 일본 교토',
    taiwanAlt: '샘플 스탬프: 대만 타이베이'
  },

  plan: {
    eyebrow: '07 & 08 · 일정 + 둘러보기',
    title: '하루하루 계획하거나,\n다른 사람의 계획에서 시작하세요.',
    body: '저장한 장소를 일자별 일정으로 바꾸세요. 계획, 숙소, 항공편을 한곳에서 관리하고, 공유하면 친구도 함께 편집할 수 있어요. 둘러보기에서 다른 사람이 이미 계획한 여행을 찾아 내 여행에 추가하고 나만의 일정으로 만들 수도 있습니다.',
    points: [
      '계획, 숙소, 항공편을 하나의 일자별 타임라인에',
      '아직 예약할 항목을 한눈에 보는 예약 체크리스트',
      '지금 일정과 다음 일정을 보여 주는 홈 화면 위젯',
      '둘러보기: 나라와 기간으로 계획을 찾아 내 여행에 추가'
    ],
    widget: {
      day: '2/10일차 · 골든 서클',
      label: '위젯',
      nowLabel: '지금 · 09:30',
      now: '싱벨리어 국립공원',
      nextLabel: '다음 · 12:30',
      next: '게이시르 & 스트로퀴르',
      later: '오늘 이따가: 굴포스',
      more: '+1개 더'
    },
    discover: {
      kicker: '둘러보기 · 아이슬란드',
      length: '10일',
      title: '아이슬란드 10일 로드 트립',
      byline: 'Travo(@travo) · 999명이 추가',
      cta: '내 여행에 추가'
    }
  },

  how: {
    eyebrow: '여행 경로',
    title: '한 번만 설정하세요. 그다음엔 떠나기만 하면 돼요.',
    steps: [
      {
        tag: '홈 베이스',
        title: '홈 베이스 알려 주기',
        body: '그래야 Travo가 언제 여행 중이고 언제 돌아왔는지 알 수 있어요.'
      },
      {
        tag: '출발',
        title: '위치 권한은 한 번만',
        body: '배터리 걱정 없는 백그라운드 기록. 설정에서 언제든 끌 수 있어요.'
      },
      {
        tag: '이동 중',
        title: '집을 나서기',
        body: '여행이 자동으로 시작되고, 지나는 도시와 나라가 차례로 기록됩니다.'
      },
      {
        tag: '도착',
        title: '스탬프, 계획, 공유',
        body: '최고의 순간에 체크인하고, 여권을 채우고, 친구와 다음 여행을 계획하세요.'
      }
    ],
    note: '이미 다녀온 여행이 있나요? Travo는 사진 보관함에서 지난 여행을 찾아낼 수 있어요. 각 사진을 찍은 위치만 읽으며, 사진은 업로드되거나 저장되지 않습니다.'
  },

  friends: {
    eyebrow: '05 · 친구',
    title: '멀리 있어도, 함께 여행해요.',
    body: '친구를 추가하면 마지막으로 체크인한 곳이 지도에 바로 표시됩니다. QR 코드로 스탬프 카드를 주고받거나 프로필 링크를 보내 보세요. 스캔해서 여정을 따라가세요.',
    to: '받는 사람: 함께 여행한 모든 이에게',
    from: '보내는 사람: app.travolabs.com/@travo',
    cta: '데모 영상 보기',
    caption: '당신이 다녀간 모든 곳에서 보내는 안부',
    mapAlt: '여권, 카메라, 여행 기념품이 놓인 세계 지도'
  },

  cta: {
    eyebrow: '항공우편 · 퍼스트 클래스',
    title: '당신의 여권이 기다리고 있어요.',
    body: 'Travo를 무료로 다운로드하세요. 매일의 일상, 주말 나들이, 다음에 떠날 여행까지 모두 한곳에.'
  },

  footer: {
    tagline: '모든 여정을 기억하고, 다음 여행을 계획하세요.',
    product: '제품',
    community: '커뮤니티',
    legal: '법적 고지',
    privacy: '개인정보 처리방침',
    terms: '이용약관',
    rights: 'TRAVO LABS. ALL RIGHTS RESERVED.'
  },

  about: {
    eyebrow: 'Travo Labs 소개',
    titleLead: '삶은 빠르게 흘러가도,',
    titleAccent: 'Travo가 기억합니다.',
    intro: [
      '삶을 나답게 만드는 장소와 순간을 오래 간직할 수 있도록 Travo를 만들었습니다. 익숙한 골목부터 먼 도시까지, Travo가 조용히 여정을 기록하니 당신은 지금 이 순간에 집중할 수 있어요.',
      '여행은 자동으로 시작됩니다. 방문하는 나라마다 여행 여권에 스탬프가 하나씩 찍히고, 저장한 장소는 다음 모험의 출발점이 됩니다.',
      'Travo를 열어 다녀온 곳을 돌아보고, 친구들의 여행 소식을 확인하고, 다음 여행지를 계획해 보세요.'
    ],
    photoAlt: '밤의 도시 스카이라인',
    caption: '여행길에서 보내는 안부',
    captionTag: 'NO. 00 · ABOUT',
    stampLabel: '항공우편',
    missionEyebrow: '우리의 미션',
    missionLead: '삶의 여정을 간직하는 일을 ',
    missionAccent: '쉽고',
    missionTail: ' 의미 있게.',
    salutation: '여행자님께,',
    letter: [
      '가장 좋은 여행 앱은 쓰고 있다는 사실조차 잊게 되는 앱이라고 믿습니다. Travo는 당신이 집을 나서는 순간을 알아채 여행을 시작하고, 우리 동네 골목부터 지구 반대편까지 지나온 동네, 도시, 나라를 모두 기록합니다.',
      '나머지는 당신이 고르면 됩니다. 소중했던 순간의 체크인 몇 개, 스탬프로 채워지는 여권, 친구와 함께 세우거나 다른 여행자에게서 빌려 온 계획까지. 당신은 여행에만 집중하세요. 기록은 Travo가 맡을게요. 몇 년이 지나도 크고 작은 모든 모험이 그 일이 있었던 바로 그 자리에 남아 있을 거예요.'
    ],
    signature: '— Travo',
    whyTitle: '왜 Travo일까요?',
    whyBody: '도장 꾹 찍어 약속하는 네 가지.',
    reasons: [
      {
        title: '여행이 알아서 시작돼요',
        body: '홈 베이스를 떠나면 여행이 자동으로 시작됩니다. 방문한 도시와 나라가 모두 기록되고, 여권에는 스탬프가 쌓여요.'
      },
      {
        title: '간직할 순간은 내가 골라요',
        body: '하루 최대 세 번의 체크인. 서 있던 자리에 사진이 핀으로 남아, 두고두고 다시 읽고 싶은 여행 일기가 됩니다.'
      },
      {
        title: '함께 계획해요',
        body: '장소를 저장하고, 친구와 함께 편집하는 일자별 일정을 만들거나, 다른 여행자가 공개한 계획에서 시작해 보세요.'
      },
      {
        title: '처음부터 개인정보 보호',
        body: '위치 기록은 언제든 끌 수 있어요. 지난 여행 찾기는 사진을 찍은 위치만 읽으며, 사진은 휴대폰 밖으로 나가지 않습니다.'
      }
    ],
    ctaEyebrow: '다음 소인',
    ctaTitle: '당신은 살아가세요. 기록은 저희가 할게요.',
    ctaBody: 'Travo는 iOS와 Android에서 무료입니다. 권한을 한 번만 허용하면 지도가 저절로 채워지기 시작해요.',
    breadcrumbAbout: '회사 소개'
  },

  schema: {
    appDescription:
      '손 하나 까딱하지 않아도 되는 여행 기록, 여권, 여행 계획 앱. 홈 베이스를 떠나면 여행이 자동으로 시작되고, 방문한 도시와 나라가 모두 기록되며, 새로운 나라마다 여권에 스탬프가 찍힙니다. 하루 최대 세 번 순간을 체크인하고, 친구와 일자별 일정을 계획하거나, 둘러보기에서 공개된 계획으로 시작하세요.',
    featureList: [
      '홈 베이스를 떠나면 자동으로 시작되는 여행',
      '체크인: 하루 최대 세 번의 사진 순간',
      '방문한 나라마다 스탬프가 찍히고 비행 거리가 쌓이는 여행 여권',
      '숙소, 항공편, 공동 편집을 지원하는 일자별 일정',
      '공개된 여행 계획을 둘러보고 내 여행에 추가',
      'Google 지도 가져오기를 지원하는 장소 리스트',
      '친구 지도와 QR 스탬프 카드',
      '사진 위치로 지난 여행 찾기',
      '홈 화면 위젯과 월간 리캡'
    ],
    videoName: 'Travo 살펴보기: 자동 여행 기록 앱 데모',
    videoDescription:
      'Travo가 매일의 이동과 큰 모험을 자동으로 기록하고, 다녀온 모든 곳을 인터랙티브 지도로 만들어 가는 모습을 확인하세요.'
  },

  notFound: {
    title: '페이지를 찾을 수 없습니다 | Travo',
    eyebrow: '수취인 불명',
    heading: '존재하지 않는 주소입니다.',
    body: '찾으시는 페이지가 이동했을 수 있어요. 홈으로 돌아가 그곳에서 다시 길을 찾아보세요.',
    cta: '홈으로 돌아가기'
  },

  faqSection: {
    eyebrow: '자주 묻는 질문',
    title: '궁금한 점, 답해 드려요.',
    body: 'Travo가 하는 일, 비용, 그리고 내 여행을 누가 볼 수 있는지 간단히 정리했어요.'
  },

  faq: [
    {
      question: 'Travo는 어떤 앱인가요?',
      answer:
        'Travo는 Travo Labs가 만든 무료 여행 기록 앱으로, iOS와 Android에서 사용할 수 있습니다. 홈 베이스를 떠나면 여행이 자동으로 시작되어 방문한 도시와 나라가 모두 기록되고, 새로운 나라마다 여권에 스탬프가 찍힙니다. 친구와 함께 일자별 여행 일정도 계획할 수 있습니다.'
    },
    {
      question: 'Travo는 한국어를 지원하나요?',
      answer:
        '네. Travo 앱과 이 웹사이트 모두 한국어를 지원하며, 영어, 번체 중국어, 일본어, 태국어로도 이용할 수 있습니다.'
    },
    {
      question: 'Travo는 여행을 어떻게 기록하나요?',
      answer:
        'Travo는 배터리 효율이 좋은 백그라운드 위치 기록을 사용합니다. 홈 베이스를 설정해 두면 그곳을 떠날 때 여행이 자동으로 시작되고, 지나는 동네, 도시, 나라가 모두 기록돼요. 따로 입력할 필요가 없습니다.'
    },
    {
      question: 'Travo는 무료인가요?',
      answer: '네. Travo는 iOS와 Android에서 무료로 다운로드하고 사용할 수 있습니다.'
    },
    {
      question: 'Travo를 쓰면 배터리가 빨리 닳나요?',
      answer: '아니요. 백그라운드 기록은 배터리에 맞게 최적화되어 있으며, 설정에서 언제든 끌 수 있습니다.'
    },
    {
      question: '체크인이 무엇인가요?',
      answer:
        '간직하고 싶은 순간을 하루 최대 세 번까지 체크인할 수 있어요. 사진을 더하면 지도가 그때 서 있던 곳을 기억하고, 이것들이 모여 나만의 여행 일기가 됩니다.'
    },
    {
      question: 'Travo 여권이 무엇인가요?',
      answer:
        '방문하는 나라마다 여권에 스탬프가 찍힙니다. 해마다 한 페이지씩 방문한 나라와 여행한 날이 집계되고, 기록한 항공편은 비행 거리(km)로 쌓입니다.'
    },
    {
      question: 'Travo에서 여행을 계획할 수 있나요?',
      answer:
        '네. 저장한 장소를 숙소와 항공편이 포함된 일자별 일정으로 만들고, 공유해서 친구와 함께 편집하거나, 둘러보기에서 공개된 계획을 내 여행에 추가할 수 있습니다.'
    },
    {
      question: 'Travo를 설치하기 전에 다녀온 여행도 추가할 수 있나요?',
      answer:
        '네. 지난 여행 찾기 기능이 사진을 찍은 위치를 읽어 이전 여행을 제안합니다. 사진은 업로드되거나 저장되지 않습니다.'
    },
    {
      question: '내 여행은 누가 볼 수 있나요?',
      answer:
        '당신이 정합니다. 위치 데이터는 비공개이며, 나를 팔로우할 친구는 직접 고를 수 있고, 위치 기록은 설정에서 언제든 끌 수 있습니다.'
    }
  ]
};
